/**
 * Rocket Launcher RL-3 — install path controller (no UI).
 *
 * Android/Chrome: capture beforeinstallprompt → promptNativeInstall().
 * iOS: open a public Shortcuts URL (config, else locked RL-0 default).
 * Standalone: treat as already installed (CTA hidden in RL-4).
 *
 * Config (public/config.js / window / global `config`):
 *   VUE_APP_HOME_SCREEN_ENABLED — default true when unset
 *   VUE_APP_HOME_SCREEN_IOS_SHORTCUT_URL — preferred; falls back to LOCKED_IOS_SHORTCUT_URL
 */

/** RL-0 locked public Shortcut — used when env config omits the key (Prd regression). */
export const LOCKED_IOS_SHORTCUT_URL =
  'https://www.icloud.com/shortcuts/74c68771082041abae16b3c3f341f2b8'

let deferredPrompt = null
let capturing = false

function readConfig (key) {
  if (typeof window !== 'undefined' && window[key] != null && window[key] !== '') {
    return window[key]
  }
  // Webpack-injected / root config.js shape used by axios/store
  if (typeof config !== 'undefined' && config && config[key] != null && config[key] !== '') {
    return config[key]
  }
  return undefined
}

export function isHomeScreenEnabled () {
  const raw = readConfig('VUE_APP_HOME_SCREEN_ENABLED')
  if (raw === undefined || raw === null || raw === '') return true
  if (raw === false || raw === 'false' || raw === '0') return false
  return true
}

export function iosShortcutUrl () {
  const url = readConfig('VUE_APP_HOME_SCREEN_IOS_SHORTCUT_URL')
  if (typeof url === 'string' && url.trim()) return url.trim()
  return LOCKED_IOS_SHORTCUT_URL
}

/**
 * True when launched from a Home Screen tile (no browser chrome).
 */
export function isStandalone () {
  if (typeof window === 'undefined') return false
  try {
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
      return true
    }
  } catch (e) { /* ignore */ }
  // iOS Safari legacy
  if (typeof navigator !== 'undefined' && navigator.standalone === true) {
    return true
  }
  return false
}

/**
 * iPhone / iPad / iPod (Safari or in-app browsers that need the Shortcut path).
 */
export function isIosLike () {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent || ''
  if (/iPhone|iPad|iPod/i.test(ua)) return true
  // iPadOS 13+ desktop UA with touch
  if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) return true
  return false
}

/**
 * Listen once for beforeinstallprompt; stash for the CTA. Call at app boot.
 */
export function captureInstallPrompt () {
  if (typeof window === 'undefined') return
  if (capturing) return
  capturing = true

  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault()
    deferredPrompt = event
    notifyInstallState()
  })

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null
    notifyInstallState()
  })
}

const installListeners = new Set()

function notifyInstallState () {
  installListeners.forEach((cb) => {
    try { cb() } catch (e) { /* ignore */ }
  })
}

/** RL-4: subscribe so CTA re-renders when the deferred prompt arrives/clears. */
export function subscribeInstallState (callback) {
  if (typeof callback !== 'function') return () => {}
  installListeners.add(callback)
  return () => { installListeners.delete(callback) }
}

export function canNativeInstall () {
  return !!deferredPrompt
}

/**
 * Fire the Chromium install sheet. Resolves { ok, outcome } where outcome is
 * 'accepted' | 'dismissed' | 'unavailable'.
 */
export async function promptNativeInstall () {
  if (!deferredPrompt) {
    return { ok: false, outcome: 'unavailable' }
  }
  const promptEvent = deferredPrompt
  deferredPrompt = null
  notifyInstallState()
  try {
    promptEvent.prompt()
    const choice = await promptEvent.userChoice
    const outcome = (choice && choice.outcome) || 'dismissed'
    return { ok: outcome === 'accepted', outcome }
  } catch (e) {
    return { ok: false, outcome: 'unavailable' }
  }
}

/**
 * Open the locked public iCloud Shortcut (same tab on iOS — popups often blocked).
 */
export function openIosShortcut () {
  const url = iosShortcutUrl()
  if (!url || typeof window === 'undefined') {
    return { ok: false, reason: 'missing-url' }
  }
  // Prefer same-tab navigation on iOS: window.open is frequently blocked and
  // looks like a dead Add button. Desktop/local can still try a new tab.
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || ''
  const ios = /iPhone|iPad|iPod/i.test(ua)
  if (ios) {
    window.location.href = url
    return { ok: true, reason: 'navigated' }
  }
  const win = window.open(url, '_blank', 'noopener,noreferrer')
  if (!win) {
    window.location.href = url
  }
  return { ok: true, reason: 'opened' }
}

/**
 * Which path RL-4 should take for the Rocket Launcher button.
 * @returns {'installed'|'native'|'ios-shortcut'|'unsupported'|'disabled'}
 */
export function installPath () {
  if (!isHomeScreenEnabled()) return 'disabled'
  if (isStandalone()) return 'installed'
  if (canNativeInstall()) return 'native'
  if (isIosLike() && iosShortcutUrl()) return 'ios-shortcut'
  if (isIosLike()) return 'unsupported'
  // Desktop / Android without deferred prompt yet — still show CTA that no-ops
  // or waits; RL-4 can label as unsupported until prompt arrives.
  return canNativeInstall() ? 'native' : 'unsupported'
}

/**
 * One-shot action for the CTA click. Prefer native, else iOS Shortcut.
 */
export async function runInstallAction () {
  const path = installPath()
  if (path === 'installed' || path === 'disabled') {
    return { path, ok: false }
  }
  if (path === 'native' || canNativeInstall()) {
    const result = await promptNativeInstall()
    return { path: 'native', ...result }
  }
  if (isIosLike()) {
    const result = openIosShortcut()
    return { path: 'ios-shortcut', ...result }
  }
  return { path: 'unsupported', ok: false }
}
